import { useEffect, useRef } from 'react';
import { ListEmptyState } from '@overlayqa/shared/ui';
import { CHROME_EXTENSION_URL } from '../../lib/constants';
import { capture } from '../../lib/analytics';
import { captureIssuesLabel } from '../../lib/captureIssuesUrl';

/**
 * The Issues empty state: what a user with zero issues reads first.
 *
 * Lives here rather than inline in IssuesPage.tsx so it can be rendered in a
 * test. IssuesPage's own guards are source-level because rendering the whole
 * page needs the entire router + query + Clerk surface (see
 * IssuesPage.settingsGear.source.test.ts); a component boundary buys a real
 * behavioural test instead.
 *
 * WHY IT LEADS WITH TEAM REVIEW (2026-08-20 incident, fixed 2026-08-25):
 * this state used to render, whenever `extensionDetected` was false, the
 * sentence "Install the Chrome extension to start capturing issues with full
 * technical context." and nothing else. That named the extension as the only
 * way to file an issue, which is false twice over:
 *
 *   1. Team-review capture needs no extension, and the control that opens it
 *      is already on this page. The route is /projects/:projectId/issues, so a
 *      project is always in scope and `Capture Issues` (LiveReviewModal) is
 *      always available beside this empty state.
 *   2. `extensionDetected === false` does not mean "not installed". Per
 *      useExtensionDetection.ts's own docstring, a tab opened before install,
 *      or one whose content script was torn down by an extension reload, reads
 *      false at a user whose extension is running fine.
 *
 * A real customer read that sentence three minutes after onboarding had routed
 * her down the no-extension team-review path, clicked it (it was a <p>, so
 * nothing happened), and never captured an issue. So the reachable action
 * leads and is a real button; the extension is demoted to the optional second
 * path it actually is. Pinned by __tests__/IssuesEmptyState.test.tsx.
 *
 * Its label is worded to match the toolbar control it mirrors in
 * IssuesPage.tsx verbatim; the two must not drift. That is now a shared
 * function rather than a promise in prose (`captureIssuesLabel`), because the
 * prose did not hold: when the toolbar learned to name its target on
 * 2026-09-10, this button kept saying `Capture Issues` for the same click.
 */
export interface IssuesEmptyStateProps {
  extensionDetected: boolean;
  /** A Viewer can read this project but cannot capture issues in it. */
  readOnly?: boolean;
  /**
   * Opens the team-review capture flow (LiveReviewModal). Omitted when there
   * is no project in scope, in which case no primary button is rendered rather
   * than a dead one.
   */
  onCapture?: () => void;
  /**
   * True while useExtensionDetection's DOM probe has not answered yet. It
   * matters because the hook returns `detected: false, checking: true` on the
   * FIRST render even for a user whose extension is running (the probe is an
   * effect, and a child's effect runs before the parent's; measured sequence:
   * "detected=false checking=true" then "detected=true checking=false").
   *
   * Without this, both the install prompt and its telemetry fire at extension
   * users, inflating the very metric this component added to count people
   * hitting the extension-only dead end.
   */
  extensionChecking?: boolean;
  /**
   * The page the capture will open, so this button names it exactly as the
   * toolbar's does. Undefined/null keeps the shared `Capture Issues` fallback.
   */
  targetUrl?: string | null;
}

export function IssuesEmptyState({ extensionDetected, extensionChecking = false, onCapture, targetUrl, readOnly = false }: IssuesEmptyStateProps) {
  // Surface-tagged so this state's exposure is finally measurable: before the
  // fix it emitted nothing at all, so nobody could see how many people read
  // the extension-only claim. Reuses the existing events with a new `source`
  // rather than minting new ones, which keeps the analytics boundary to a
  // step-up splittable by `source`.
  const offerInstall = !readOnly && !extensionChecking && !extensionDetected;
  const firedRef = useRef(false);
  useEffect(() => {
    if (offerInstall && !firedRef.current) {
      firedRef.current = true;
      capture('extension_install_banner_shown', { source: 'issues_empty_state' });
    }
  }, [offerInstall]);

  return (
    <ListEmptyState className="card-sm" title={readOnly ? 'No issues yet' : 'Create your first issue'}
      hint={readOnly ? 'You have view-only access to this project.' : extensionDetected
          ? 'Open the OverlayQA extension on any page, click an element, and capture an issue. You can also start a team review right here.'
          : 'Start a team review to click elements on your site and file issues, right here. No extension needed.'}
      action={<>
      {!readOnly && onCapture && (
        <button
          type="button"
          className="btn-primary text-sm inline-flex items-center gap-2 mt-4"
          onClick={onCapture}
          title={targetUrl ? `Opens ${targetUrl}` : undefined}
        >
          <span className="truncate max-w-[18rem]">{captureIssuesLabel(targetUrl)}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M17 7H7M17 7v10" /></svg>
        </button>
      )}

      {offerInstall && (
        <p className="text-xs text-gray-400 mt-4" style={{ textWrap: 'pretty' }}>
          Prefer capturing as you browse?{' '}
          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
            onClick={() => capture('dashboard_extension_install_click', { source: 'issues_empty_state' })}
          >
            Install the Chrome&nbsp;extension
          </a>
        </p>
      )}
      </>} />
  );
}
